import * as React from "react";
import styled from "@emotion/styled";
import MuiCheckbox from "@material-ui/core/Checkbox";
import { FormControlLabel, makeStyles } from "@material-ui/core";
import { BaseStyleAttrs } from "@/theme";
import { CheckboxColorExtras } from "@/utils/contract";

type MarginTypes =
  | "margin"
  | "margin-top"
  | "margin-right"
  | "margin-bottom"
  | "margin-left"
  | "marginTop"
  | "marginRight"
  | "marginBottom"
  | "marginLeft";

export type CheckboxProps = {
  onChange: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  checked: boolean;
  name?: string;
  text?: string;
  isCenter?: boolean;
  labelfontWeight?: string;
  labelFontSize?: string;
  checkboxColor?: CheckboxColorExtras;
  extraMargins?: { [key in MarginTypes]?: string | number };
  isNewTheme?: boolean;
  isBorder?: boolean;
} & BaseStyleAttrs &
  React.InputHTMLAttributes<HTMLInputElement>;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  &.inline {
    flex-direction: row;
  }
`;

const InputWrap = styled.div`
  display: flex;
  display: nowrap;
  align-items: center;

  &.center {
    justify-content: center;
  }
`;

const Checkbox = ({
  id,
  name,
  value,
  checked,
  onChange,
  text,
  isCenter,
  checkboxColor,
  extraMargins,
  labelFontSize,
  labelfontWeight,
  isNewTheme,
  isBorder,
  ...props
}: CheckboxProps) => {
  const hasBorder = !!checkboxColor?.border;
  const color: CheckboxColorExtras = {
    text: checkboxColor?.text || "#666666",
    background: checkboxColor?.background || "#ffffff",
    checkbox: {
      checked: checkboxColor?.checkbox?.checked || "#0275ff",
      unchecked: checkboxColor?.checkbox?.unchecked || "#8e8e9b",
    },
    border: checkboxColor?.border,
  };

  let checkboxStyles = makeStyles({
    root: {
      color: color.checkbox?.unchecked,
      transform: "scale(1.25)",
      padding: 0,
      margin: "3px 10px 3px 4px",
      height: "14px",

      "&.Mui-IconButton": {
        width: "20px",
      },

      "&:hover": {
        color: color.checkbox?.unchecked,
        backgroundColor: "rgba(0, 0, 0, 0)",
      },

      "&.Mui-checked": {
        color: color.checkbox?.checked,

        "&:hover": {
          color: color.checkbox?.checked,
          backgroundColor: "rgba(0, 0, 0, 0)",
        },
      },

      "&.Mui-disabled": {
        color: "rgba(0, 0, 0, 0.26)",
      },
    },
  });

  if (isNewTheme)
    checkboxStyles = makeStyles({
      root: {
        color: color.checkbox?.unchecked,
        transform: "scale(1.25)",
        padding: 0,
        margin: "3px 10px 3px 4px",
        height: "14px",

        "&:not($checked) .MuiIconButton-label:after": {
          content: '""',
          height: 12,
          width: 12,
          position: "absolute",
          backgroundColor: checked ? "white" : color.checkbox?.unchecked,
          zIndex: -1,
        },

        "&.Mui-IconButton": {
          width: "20px",
        },

        "&:hover": {
          color: color.checkbox?.unchecked,
          backgroundColor: "rgba(0, 0, 0, 0)",
        },

        "&.Mui-checked": {
          color: color.checkbox?.checked,
          backgroundColor: "rgba(255, 255, 255, 255)",

          "&:hover": {
            color: color.checkbox?.checked,
            backgroundColor: "rgba(0, 0, 0, 0)",
          },
        },

        "&.Mui-disabled": {
          color: isNewTheme ? color.checkbox?.unchecked : "rgba(0, 0, 0, 0.26)",
        },
      },
    });
  const checkboxClasses = checkboxStyles();

  const sourceLabelStyles: Record<string, any> = {
    root: {
      marginLeft: 0,
      marginRight: 0,
      backgroundColor: color.background,
      border: color.border,
      padding: isBorder ? "13.4px 90.175px" : hasBorder ? "5px" : ""
    },
    label: {
      fontSize: labelFontSize ? labelFontSize : "14px",
      fontWeight: labelfontWeight ? labelfontWeight : "bold",
      color: color.text,
      fontFamily: "inherit",
      lineHeight: "inherit",
    },
  };
  if (extraMargins) {
    sourceLabelStyles.root = { ...sourceLabelStyles.root, ...extraMargins };
  }
  let labelStyles = makeStyles(sourceLabelStyles);
  if(isBorder){
     labelStyles = makeStyles({ ...sourceLabelStyles, root: {
      ...sourceLabelStyles.root,
      "@media (max-width: 637px)": {
      ...sourceLabelStyles.root,
      display: "flex",
      justifyContent: "center",
      width: "100%",
      whiteSpace: "nowrap"
      }
    }});
  }

  const labelClasses = labelStyles();

  return (
    <Wrapper>
      <InputWrap className={isCenter ? "center" : ""}>
        <FormControlLabel
          control={
            <MuiCheckbox
              inputProps={{ "aria-label": text }}
              classes={{ ...checkboxClasses }}
              checked={checked}
              name={name}
              value={value}
              onChange={onChange}
              disableRipple
              disableFocusRipple
              disableTouchRipple
              disabled={props.disabled}
            />
          }
          label={text}
          classes={{ ...labelClasses }}
        />
      </InputWrap>
    </Wrapper>
  );
};

export default Checkbox;
